import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AppShell } from "@/components/bi/AppShell";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FleetOverview } from "@/components/bi/FleetOverview";
import { WelcomePack } from "@/components/bi/WelcomePack";
import { useAuth } from "@/context/AuthContext";
import { isCrossTenant, isPlatformAdmin, roleLabel } from "@/lib/roles";
import {
  Building2, Search, ArrowRight, Database, LayoutDashboard, Users, ShieldCheck, Link2, Activity, Check, Gauge, Gift,
} from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const m = Math.round(Math.max(0, Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d}d ago` : new Date(iso).toLocaleDateString();
}

const Metric = ({ icon: Icon, value, label }) => (
  <span className="flex items-center gap-1 text-[11px] text-[color:var(--ink-3)]" title={label}>
    <Icon className="w-3.5 h-3.5" />{value}
  </span>
);

// Item 24: the Eazy-side console — every tenant Eazy staff can work inside.
export default function EazyConsole() {
  const nav = useNavigate();
  const { user } = useAuth();
  const staff = isCrossTenant(user?.role);
  const canSetPolicy = isPlatformAdmin(user?.role);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (!staff) return;
    api.get("/managed-sources/console").then(({ data }) => setData(data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "This console is for Eazy staff only"); setData({ tenants: [], activity: [] }); });
  }, [staff]);
  useEffect(() => { load(); }, [load]);

  const enter = async (tid) => {
    setBusy(tid);
    try {
      await api.post("/auth/switch-tenant", { tenant_id: tid });
      window.location.href = "/";
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not enter that tenant"); setBusy(""); }
  };

  const flipPolicy = async (tid, v) => {
    try {
      await api.put(`/tenants/${tid}/source-policy`, { allow_tenant_data_sources: v });
      setData((d) => ({ ...d, tenants: d.tenants.map((t) => (t.id === tid ? { ...t, allow_tenant_data_sources: v } : t)) }));
      toast.success(v ? "This tenant may add its own data sources" : "Only Eazy provisions sources for this tenant");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the policy"); }
  };

  if (user && !staff) return <Navigate to="/" replace />;
  if (data === null) return <AppShell title="Eazy Console"><Loading label="Loading tenants" /></AppShell>;

  const tenants = (data.tenants || []).filter((t) => {
    const s = q.trim().toLowerCase();
    return !s || (t.name || "").toLowerCase().includes(s) || (t.id || "").toLowerCase().includes(s);
  });

  return (
    <AppShell title="Eazy Console">
      <div className="px-5 md:px-8 py-6 max-w-6xl space-y-7" data-testid="eazy-console">
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[color:var(--ink-3)]">
            <ShieldCheck className="w-3.5 h-3.5" />{roleLabel(data.me?.role || user?.role)}
          </div>
          <h1 className="font-display text-3xl text-[color:var(--ink)] mt-1.5">Every tenant, one console</h1>
          <p className="text-sm text-[color:var(--ink-3)] mt-2 max-w-2xl">
            Build a source or a dashboard here, then provision it into a tenant — copied, or live-linked so your
            edits keep flowing. Enter a tenant to work inside it; everything you do there is logged as Eazy activity.
          </p>
        </div>

        <Tabs defaultValue="tenants">
          <TabsList className="bg-[color:var(--surface-2)]">
            <TabsTrigger value="tenants" data-testid="eazy-tab-tenants"><Building2 className="w-4 h-4 mr-1.5" />Tenants</TabsTrigger>
            <TabsTrigger value="fleet" data-testid="eazy-tab-fleet"><Gauge className="w-4 h-4 mr-1.5" />Fleet usage</TabsTrigger>
            <TabsTrigger value="pack" data-testid="eazy-tab-pack"><Gift className="w-4 h-4 mr-1.5" />Welcome pack</TabsTrigger>
          </TabsList>

          <TabsContent value="tenants" className="mt-4 space-y-7">
        <div className="relative max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[color:var(--ink-3)]" />
          <Input data-testid="eazy-tenants-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search tenants…" className="pl-8 bg-[color:var(--surface)]" />
        </div>
        <section>
          <h2 className="text-base md:text-lg font-display text-[color:var(--ink)] mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[color:var(--brand-ocean)]" />Tenants
            <span className="text-xs text-[color:var(--ink-3)] font-sans">{tenants.length}</span>
          </h2>
          <div className="space-y-2" data-testid="eazy-tenants-list">
            {tenants.map((t) => (
              <div key={t.id} data-testid={`eazy-tenant-${t.id}`}
                className="glass-card px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="min-w-[180px] flex-1">
                  <span className="text-sm text-[color:var(--ink)] flex items-center gap-2">
                    {t.name}
                    {t.active && <Badge variant="outline" className="text-[9px] py-0 text-[color:var(--brand-mint)] border-[color:var(--brand-mint)]"><Check className="w-2.5 h-2.5 mr-0.5" />you are here</Badge>}
                  </span>
                  <span className="flex items-center gap-3 mt-1">
                    <Metric icon={Users} value={t.users} label="users" />
                    <Metric icon={LayoutDashboard} value={t.dashboards} label="dashboards" />
                    <Metric icon={Database} value={t.datasets} label="datasets" />
                    <Metric icon={ShieldCheck} value={t.managed_sources} label="Eazy-managed sources" />
                    <Metric icon={Link2} value={t.linked_dashboards} label="live-linked dashboards" />
                  </span>
                </span>
                <span className="flex items-center gap-2 text-[11px] text-[color:var(--ink-3)]" title="Tenant can create their own data sources">
                  own sources
                  <Switch data-testid={`eazy-policy-${t.id}`} checked={t.allow_tenant_data_sources}
                    disabled={!canSetPolicy} onCheckedChange={(v) => flipPolicy(t.id, v)}
                    className={!canSetPolicy ? "opacity-50 cursor-not-allowed" : undefined} />
                </span>
                <Button data-testid={`eazy-enter-${t.id}`} size="sm" variant="outline" disabled={busy === t.id || t.active}
                  onClick={() => enter(t.id)} className="bg-[color:var(--surface)]">
                  {t.active ? "Working here" : <>Enter tenant <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
                </Button>
              </div>
            ))}
            {!tenants.length && <p className="text-xs text-[color:var(--ink-3)] px-1">No tenants match “{q}”.</p>}
          </div>
        </section>

        <section>
          <h2 className="text-base md:text-lg font-display text-[color:var(--ink)] mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-[color:var(--brand-ocean)]" />Recent Eazy activity
          </h2>
          <div className="glass-card divide-y divide-[color:var(--glass-border)]" data-testid="eazy-activity">
            {(data.activity || []).map((a) => (
              <div key={a.id} className="px-4 py-2 flex items-center gap-3 text-xs">
                <span className="flex-1 min-w-0 truncate text-[color:var(--ink-2)]">
                  <b className="text-[color:var(--ink)]">{a.user_email}</b> · <code className="text-[10px]">{a.action}</code>
                  {a.details?.name ? ` — “${a.details.name}”` : ""}
                </span>
                <button onClick={() => nav("/admin")} className="text-[10px] text-[color:var(--brand-ocean)] hover:underline shrink-0">{a.tenant_name}</button>
                <span className="text-[10px] text-[color:var(--ink-3)] whitespace-nowrap">{relTime(a.ts)}</span>
              </div>
            ))}
            {!(data.activity || []).length && <p className="px-4 py-8 text-center text-xs text-[color:var(--ink-3)]">No Eazy staff activity yet.</p>}
          </div>
        </section>
          </TabsContent>

          <TabsContent value="fleet" className="mt-4"><FleetOverview onEnter={enter} /></TabsContent>
          <TabsContent value="pack" className="mt-4"><WelcomePack tenants={data.tenants || []} /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
